import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "@iconify/react";
import styles from "./ReviewTab.module.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.mil-168.com"
).replace(/\/+$/, "");

const STATUS_LABEL = {
  PENDING: "รอตรวจสอบ",
  APPROVED: "อนุมัติแล้ว",
  HIDDEN: "ซ่อน",
};

function StarPicker({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  const display = hovered || value;
  return (
    <div className={styles.stars}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`${styles.starBtn} ${display >= n ? styles.filled : ""}`}
          onClick={() => onChange(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          aria-label={`${n} ดาว`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function StarDisplay({ value, size = 16 }) {
  return (
    <div className={styles.historyStars}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          style={{
            fontSize: size,
            color: n <= value ? "#facc15" : "rgba(255,255,255,0.2)",
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function resolveUserId() {
  try {
    const stored = localStorage.getItem("user");
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.id)
        return {
          id: parsed.id,
          username: parsed.username || parsed.name || "",
        };
    }
  } catch (_) {}
  const id = localStorage.getItem("userId");
  return id ? { id, username: "" } : null;
}

function getAuthHeader() {
  const token =
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("sessionToken");
  if (!token) return {};
  const bearer =
    token.startsWith("Bearer ") || token.startsWith("Basic ")
      ? token
      : `Bearer ${token}`;
  return { Authorization: bearer };
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function ReviewTab() {
  const [activeTab, setActiveTab] = useState("write");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const userInfo = resolveUserId();

  const fetchHistory = useCallback(async () => {
    if (!userInfo?.id) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/reviews/my`, {
        params: { userId: userInfo.id },
        headers: getAuthHeader(),
      });
      setHistory(res.data?.data || []);
    } catch (_) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [userInfo?.id]);

  useEffect(() => {
    if (activeTab === "history") fetchHistory();
  }, [activeTab, fetchHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) {
      setFeedback({ type: "error", msg: "กรุณาเลือกจำนวนดาวก่อน" });
      return;
    }
    if (!userInfo?.id) {
      setFeedback({ type: "error", msg: "กรุณาเข้าสู่ระบบก่อน" });
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    try {
      await axios.post(
        `${API_BASE_URL}/reviews`,
        {
          userId: userInfo.id,
          username: userInfo.username,
          rating,
          message: message.trim(),
        },
        { headers: { ...getAuthHeader(), "Content-Type": "application/json" } },
      );
      setFeedback({
        type: "success",
        msg: "ขอบคุณสำหรับรีวิว! รอแอดมินตรวจสอบก่อนแสดงผล",
      });
      setRating(0);
      setMessage("");
    } catch (err) {
      const msg = err?.response?.data?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่";
      setFeedback({ type: "error", msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "write" ? styles.active : ""}`}
          onClick={() => setActiveTab("write")}
        >
          เขียนรีวิว
        </button>
        <button
          type="button"
          className={`${styles.tabBtn} ${activeTab === "history" ? styles.active : ""}`}
          onClick={() => setActiveTab("history")}
        >
          ประวัติรีวิว
        </button>
      </div>

      {activeTab === "write" && (
        <form className={styles.writeSection} onSubmit={handleSubmit}>
          <div className={styles.ratingRow}>
            <span className={styles.ratingLabel}>ให้คะแนน:</span>
            <StarPicker value={rating} onChange={setRating} />
          </div>
          <textarea
            className={styles.textarea}
            placeholder="แสดงความคิดเห็น... (ไม่บังคับ)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={1000}
          />
          {feedback && (
            <div className={`${styles.feedbackMsg} ${styles[feedback.type]}`}>
              {feedback.msg}
            </div>
          )}
          <button
            type="submit"
            className={styles.submitBtn}
            disabled={submitting || !rating}
          >
            {submitting ? "กำลังส่ง..." : "ส่งรีวิว"}
          </button>
        </form>
      )}

      {activeTab === "history" && (
        <>
          {historyLoading ? (
            <div className={styles.loaderWrap}>
              <div className={styles.spinner} />
            </div>
          ) : history.length === 0 ? (
            <div className={styles.emptyState}>
              <Icon icon="solar:star-shine-bold-duotone" width={40} />
              <span>ยังไม่มีประวัติรีวิว</span>
            </div>
          ) : (
            <div className={styles.historyList}>
              {history.map((r) => (
                <div key={r.id} className={styles.historyCard}>
                  <div className={styles.historyCardTop}>
                    <StarDisplay value={r.rating} />
                    <span
                      className={`${styles.historyBadge} ${styles[r.status]}`}
                    >
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </div>
                  {r.message && (
                    <div className={styles.historyMsg}>{r.message}</div>
                  )}
                  <div className={styles.historyDate}>
                    {formatDate(r.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
